import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ClassNames from 'classnames';
import { LoaderComponent } from '@dnhb/node_package';
import { dispatchMapper, INIT } from 'shared/wizard/actions';
import { WizardContainer, OutputContainer, Disclaimer } from 'shared/legacy';
import { shapeInput, shapeSettings } from '../config';
import { getSettings } from '../helpers';
import lazyWithRetry from "shared/utility/lazyWithRetry";

const wizardLandingPromise = import(
  /* webpackChunkName: "hns-wizard-landing" */
  './WizardLanding'
);
const wizardMortgagePromise = import(
  /* webpackChunkName: "hns-wizard-mortgage" */
  './WizardMortgage'
);
const wizardResultsPromise = import(
  /* webpackChunkName: "hns-wizard-results" */
  './WizardResults'
);
const wizardIncomePromise = import(
  /* webpackChunkName: "hns-wizard-income" */
  './WizardIncome'
);
const wizardSavingsPromise = import(
  /* webpackChunkName: "hns-wizard-savings" */
  './WizardSavings'
);
const outputEmptyPromise = import(
  /* webpackChunkName: "hns-output-empty" */
  './OutputEmpty'
);
const outputResultsPromise = import(
  /* webpackChunkName: "hns-output-results" */
  './OutputResults'
);

const WizardLanding = lazyWithRetry(() => wizardLandingPromise);
const WizardMortgage = lazyWithRetry(() => wizardMortgagePromise);
const WizardResults = lazyWithRetry(() => wizardResultsPromise);
const WizardIncome = lazyWithRetry(() => wizardIncomePromise);
const WizardSavings = lazyWithRetry(() => wizardSavingsPromise);
const OutputEmpty = lazyWithRetry(() => outputEmptyPromise);
const OutputResults = lazyWithRetry(() => outputResultsPromise);

const App = ({ syncing, app, settings, input, broken, framed, dispatch, pages }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const base = `${app}${framed ? '/framed' : ''}`;
  const onResultsPage = ['results', 'final'].indexOf(location.state?.page) > -1;
  
  useEffect(() => {
    navigate(
      `/${app}${framed ? '/framed' : ''}/${pages[0] + location.search}`,
      { state: { page: pages[0] }, replace: true },
    );
  }, [framed]);

  useEffect(() => {
    getSettings(res => {
      const settings = res.data || {};
      dispatch({
        type: INIT,
        payload: { settings },
        location,
        navigate,
      });
    });
  }, []);

  if (!settings) {
    return (
      <div className="tool-container placeholder with-loader loading">
        <LoaderComponent show />
      </div>
    );
  }

  if (input.show_disclaimer) {
    return (
      <div className="tool-container">
        <Disclaimer>
          <p>
            Ondanks de constante zorg en aandacht die HFBG besteedt aan de samenstelling van deze tool en de daarin
            gevraagde gegevens, kan HFBG niet instaan voor de volledigheid, juistheid of voortdurende actualiteit van de
            gegevens. HFBG aanvaardt dan ook geen aansprakelijkheid voor enigerlei directe of indirecte schade, van
            welke aard ook, die voortvloeit uit of in enig opzicht verband houdt met het gebruik van deze tool.
          </p>
          <p>
            Er wordt in de berekening geen rekening gehouden met financiële verplichtingen zoals studieschuld,
            alimentatie en erfpacht.
          </p>
          <p>
            De berekening is op basis van het herfinancieren van de aflosvrije schuld naar een annuïtaire hypotheek met
            een looptijd van 30 jaar. Daarbij gaan wij er van uit dat u geen extra aflossingen doet, geen box 3 gedeelte
            heeft en geen recht meer heeft op hypotheekrente aftrek.
          </p>
          <p>Wij rekenen met de gemiddelde debetrentevoet van minimaal 5%, die is vastgesteld door de overheid.</p>
          <p>Uw adviseur kan een exacte berekening voor u maken.</p>
        </Disclaimer>
      </div>
    );
  }

  return (
    <div
      className={ClassNames({
        'tool-container': true,
        busy: syncing,
        broken,
        'results-page': onResultsPage,
      })}
    >
      <WizardContainer>
        <Routes>
          <Route path={`/${base}/mortgage`}  element={<WizardMortgage /> } />
          <Route path={`/${base}/results`}  element={<WizardResults /> } />
          <Route path={`/${base}/income`}  element={<WizardIncome /> } />
          <Route path={`/${base}/savings`}  element={<WizardSavings /> } />
          <Route path={`/${base}/final`}  element={<WizardResults /> } />
          <Route path={`/${base}/landing`}  element={<WizardLanding /> } />
        </Routes>
      </WizardContainer>
      <OutputContainer forceRemoveLogo={onResultsPage}>
        <Routes>
          <Route path={`/${base}/results`}  element={<OutputResults /> } />
          <Route path={`/${base}/income`} element={<OutputEmpty text="Vul hiernaast uw gegevens in voor een rapportcijfer" />} />
          <Route path={`/${base}/savings`} element={<OutputEmpty text="Vul hiernaast uw gegevens in voor een rapportcijfer" />} />
          <Route path={`/${base}/final`}  element={<OutputResults /> } />
          <Route path={`/${base}/landing`} element={<OutputEmpty text="Vul hiernaast uw gegevens in" />} />
        </Routes>
      </OutputContainer>
    </div>
  );
};

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  broken: PropTypes.bool.isRequired,
  syncing: PropTypes.bool.isRequired,
  framed: PropTypes.bool.isRequired,
  settings: shapeSettings,
  input: shapeInput.isRequired,
};

App.defaultProps = {
  settings: null,
};

export default connect(
  state => ({
    app: state.app,
    framed: state.framed,
    broken: state.broken,
    syncing: state.syncing,
    output: state.output,
    input: state.input,
    pages: state.pages,
    settings: state.settings,
  }),
  dispatchMapper
)(App)
